import React from 'react';
import { IMAGE_TYPES, imageFileError, uploadLogo } from '../api/upload.js';
import { useT } from '../i18n.jsx';

// Controlled value is always a persisted URL. A local preview never enters the form payload.
export function LogoUpload({ value = '', onChange, onBlur, name, disabled = false, onUploadingChange }) {
  const { t } = useT();
  const id = React.useId();
  const inputRef = React.useRef(null);
  const operation = React.useRef(null);
  const busyCallback = React.useRef(onUploadingChange);
  busyCallback.current = onUploadingChange;
  const [uploading, setUploading] = React.useState(false);
  const [preview, setPreview] = React.useState('');
  const [error, setError] = React.useState('');
  const [uploaded, setUploaded] = React.useState(false);
  const [failedSrc, setFailedSrc] = React.useState(null);
  const src = preview || value;

  const finish = (task) => {
    clearTimeout(task.timer);
    if (task.preview) URL.revokeObjectURL(task.preview);
  };
  const cancel = () => {
    const task = operation.current;
    if (task) {
      operation.current = null;
      task.controller.abort();
      finish(task);
    }
    setPreview('');
    setUploading(false);
    busyCallback.current?.(false);
  };
  React.useEffect(() => () => {
    const task = operation.current;
    if (task) {
      operation.current = null;
      task.controller.abort();
      finish(task);
      busyCallback.current?.(false);
    }
  }, []);

  const selectFile = async (file) => {
    if (!file || disabled) return;
    const invalid = imageFileError(file);
    if (invalid) {
      setError(t(`logo.error.${invalid}`));
      return;
    }
    cancel();
    setError('');
    setUploaded(false);
    setFailedSrc(null);
    const task = { controller: new AbortController(), preview: URL.createObjectURL(file), timedOut: false };
    operation.current = task;
    setPreview(task.preview);
    setUploading(true);
    busyCallback.current?.(true);
    task.timer = setTimeout(() => {
      task.timedOut = true;
      task.controller.abort();
    }, 60_000);
    try {
      const url = await uploadLogo(file, { signal: task.controller.signal });
      if (operation.current !== task) return;
      onChange(url);
      setUploaded(true);
    } catch (err) {
      if (operation.current !== task) return;
      setError(task.timedOut ? t('logo.error.timeout') : `${t('logo.error.upload')} ${err?.message || ''}`.trim());
    } finally {
      // Aborted/replaced requests must not overwrite a newer selection or URL edit.
      if (operation.current === task) {
        operation.current = null;
        finish(task);
        setPreview('');
        setUploading(false);
        busyCallback.current?.(false);
      }
    }
  };

  const changeUrl = (url) => {
    cancel();
    setError('');
    setUploaded(false);
    setFailedSrc(null);
    onChange(url);
  };

  return (
    <div className="col gap-2" aria-busy={uploading}>
      <div className="row gap-2" style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div
          style={{ width: 64, height: 64, borderRadius: 10, border: '1px solid var(--line)', background: 'var(--bg-2)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', color: 'var(--fg-4)', fontSize: 10 }}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => { event.preventDefault(); selectFile(event.dataTransfer.files?.[0]); }}
        >
          {src && failedSrc !== src ? (
            <img src={src} alt={t('logo.preview')} style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={() => setFailedSrc(src)} />
          ) : <span aria-hidden="true">LOGO</span>}
        </div>
        <div className="col gap-2" style={{ flex: 1, minWidth: 0 }}>
          <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-sm" disabled={disabled || uploading} onClick={() => inputRef.current?.click()}>
              {uploading ? t('logo.uploading') : value ? t('logo.replace') : t('logo.upload')}
            </button>
            {uploading && <button type="button" className="btn btn-sm" onClick={cancel}>{t('logo.cancel')}</button>}
            {value && <button type="button" className="btn btn-sm" disabled={disabled} onClick={() => changeUrl('')}>{t('logo.remove')}</button>}
          </div>
          <input ref={inputRef} type="file" accept={IMAGE_TYPES.join(',')} aria-label={t('logo.upload')} hidden disabled={disabled || uploading}
            onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ''; selectFile(file); }} />
          <label htmlFor={id} style={{ fontSize: 11, color: 'var(--fg-3)' }}>{t('logo.url')}</label>
          <input id={id} className="input mono" name={name} value={value} placeholder="https://…" disabled={disabled} onBlur={onBlur}
            onChange={(event) => changeUrl(event.target.value)} aria-describedby={`${id}-hint`} style={{ width: '100%', minWidth: 0 }} />
        </div>
      </div>
      <div id={`${id}-hint`} style={{ color: 'var(--fg-3)', fontSize: 11, lineHeight: 1.6 }}>{t('logo.hint')}</div>
      {uploading && <div role="status" style={{ color: 'var(--fg-3)', fontSize: 12 }}>{t('logo.pending')}</div>}
      {uploaded && !uploading && <div role="status" style={{ color: 'var(--ok)', fontSize: 12 }}>{t('logo.uploaded')}</div>}
      {src && failedSrc === src && <div role="alert" style={{ color: 'var(--err)', fontSize: 12 }}>{t('logo.error.preview')}</div>}
      {error && <div role="alert" style={{ color: 'var(--err)', fontSize: 12 }}>{error}</div>}
    </div>
  );
}
